<template>
  <div class="mx-3 my-3">
    <AppModal v-model="showDetail" title="Notification details" size="lg" hide-footer>
      <div v-if="selectedNotification" class="detail-wrap">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <p v-if="notificationId(selectedNotification)" class="detail-row">
              <span class="detail-label">ID:</span> {{ notificationId(selectedNotification) }}
            </p>
            <p class="detail-row">
              <span class="detail-label">Receiver:</span>
              <span class="detail-value-wrap">{{ receiverDisplay(selectedNotification) }}</span>
            </p>
            <p class="detail-row">
              <span class="detail-label">Status:</span>
              <span
                v-if="fieldValue(selectedNotification, 'status')"
                :class="statusBadgeClass(fieldValue(selectedNotification, 'status'))"
              >{{ fieldValue(selectedNotification, "status") }}</span>
              <span v-else>—</span>
            </p>
            <p class="detail-row">
              <span class="detail-label">Sender:</span>
              {{ fieldValue(selectedNotification, "sender") || "—" }}
            </p>
          </div>
          <div>
            <p v-if="inferredType(selectedNotification)" class="detail-row">
              <span class="detail-label">Type:</span> {{ inferredType(selectedNotification) }}
            </p>
            <p class="detail-row">
              <span class="detail-label">Date:</span>
              {{ fieldValue(selectedNotification, "date") || "—" }}
            </p>
          </div>
        </div>
        <div class="mt-4 border-t border-gray-200 pt-3">
          <p class="font-semibold text-sm text-gray-700 mb-1">Response:</p>
          <pre class="bg-gray-50 border border-gray-200 p-3 rounded-md text-xs overflow-auto font-mono max-h-64 whitespace-pre-wrap break-all text-gray-800">{{ formattedResponse(selectedNotification) }}</pre>
        </div>
      </div>
    </AppModal>

    <div class="rounded-md border border-gray-200 bg-white p-4 mb-3">
      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-3">
        <div>
          <label class="filter-label">Search</label>
          <div class="relative">
            <i class="fa fa-search absolute left-3 top-1/2 -translate-y-1/2 text-xs text-gray-400"></i>
            <input
              v-model="filters.search"
              type="text"
              placeholder="Search by receiver"
              class="filter-input pl-8"
              @keyup.enter="searchResetPage"
            />
          </div>
        </div>
        <div>
          <label class="filter-label">Type</label>
          <select v-model="filters.type" class="filter-input">
            <option v-for="opt in typeOptions" :key="String(opt.value)" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>
        <div>
          <label class="filter-label">Status</label>
          <select v-model="filters.status" class="filter-input">
            <option v-for="opt in statusOptions" :key="String(opt.value)" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>
        <div>
          <label class="filter-label">Sender</label>
          <select v-model="filters.sender" class="filter-input">
            <option v-for="opt in senderOptions" :key="String(opt.value)" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>
        <div>
          <label class="filter-label">Sort</label>
          <select v-model="sort" class="filter-input">
            <option v-for="opt in sortOptions" :key="String(opt.value)" :value="opt.value">{{ opt.text }}</option>
          </select>
        </div>
        <div>
          <label class="filter-label">Start date</label>
          <input v-model="filters.startDateLocal" type="datetime-local" class="filter-input" />
        </div>
        <div>
          <label class="filter-label">End date</label>
          <input v-model="filters.endDateLocal" type="datetime-local" class="filter-input" />
        </div>
        <div class="flex items-end justify-end gap-2">
          <button
            class="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
            type="button"
            @click="resetFilters"
          >
            <i class="fa fa-undo mr-1"></i> Reset
          </button>
          <button class="btn-primary px-4 py-2 text-sm rounded text-white" type="button" @click="searchResetPage">
            <i class="fa fa-search mr-1"></i> Search
          </button>
        </div>
      </div>
    </div>

    <div v-if="loading" class="h-0.5 bg-primary animate-pulse mb-2"></div>

    <div class="overflow-x-auto rounded-md border border-gray-200">
      <table class="w-full leading-normal">
        <thead>
        <tr class="border-b border-gray-200">
          <th class="t-header w-12">#</th>
          <th class="t-header w-40">Receiver</th>
          <th class="t-header w-32">Status</th>
          <th class="t-header">Response</th>
          <th class="t-header w-32">Sender</th>
          <th class="t-header w-48">Date</th>
          <th class="t-header text-center w-28">Action</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, idx) in notifications"
          :key="displayKey(row, idx)"
          :class="idx % 2 ? 'border-t border-gray-200 hover:bg-gray-50' : 'border-t border-gray-200 bg-gray-50'"
        >
          <td class="t-body">{{ rowNumber(idx) }}</td>
          <td class="t-body whitespace-nowrap" :title="receiverDisplay(row)">{{ receiverDisplay(row) }}</td>
          <td class="t-body whitespace-nowrap">
            <span v-if="fieldValue(row, 'status')" :class="statusBadgeClass(fieldValue(row, 'status'))">
              {{ fieldValue(row, "status") }}
            </span>
            <span v-else>—</span>
          </td>
          <td class="t-body">
            <div
              class="max-w-[22rem] truncate"
              :class="isFailed(row) ? 'text-red-700' : 'text-gray-600'"
              :title="responseSummary(row)"
            >
              {{ responseSummary(row) }}
            </div>
          </td>
          <td class="t-body whitespace-nowrap">{{ fieldValue(row, "sender") || "—" }}</td>
          <td class="t-body whitespace-nowrap">{{ fieldValue(row, "date") || "—" }}</td>
          <td class="t-body text-center">
            <button class="btn-primary px-2 py-1 rounded-sm text-white text-sm" type="button" @click="openDetail(row)">
              View <i class="fa fa-eye mx-1"></i>
            </button>
          </td>
        </tr>
        <tr v-if="notifications.length === 0 && !loading">
          <td colspan="7" class="py-10 text-center">
            <div class="flex flex-col items-center gap-2">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-gray-50 text-gray-400">
                <i class="fa-regular fa-envelope-open text-lg"></i>
              </span>
              <p class="text-sm font-medium text-gray-700">No notifications found</p>
              <p class="text-xs text-gray-500">
                {{ hasActiveFilters ? "Try changing the filters or widening the date range." : "Messages will appear here once they are sent." }}
              </p>
            </div>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <div
      v-if="pagination.totalPages > 1 || pagination.totalElements > notifications.length"
      class="flex flex-wrap items-center justify-between gap-3 my-3"
    >
      <div class="flex flex-wrap items-center gap-2">
        <button
          type="button"
          class="min-w-24 px-4 py-2 text-sm border rounded"
          :class="currentPage > 1 ? 'border-gray-300 text-primary hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'"
          :disabled="currentPage <= 1"
          @click="previous"
        >
          Previous
        </button>
        <span class="px-2 py-1 bg-primary text-white text-sm rounded">{{ currentPage }} / {{ pagination.totalPages }}</span>
        <button
          type="button"
          class="min-w-24 px-4 py-2 text-sm border rounded"
          :class="currentPage < pagination.totalPages ? 'border-gray-300 text-primary hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'"
          :disabled="currentPage >= pagination.totalPages"
          @click="next"
        >
          Next
        </button>
      </div>
      <div class="flex flex-wrap items-center gap-2">
        <label class="text-sm text-gray">Per page</label>
        <select v-model="perPage" class="px-3 py-2 text-sm border border-gray-300 rounded" @change="onPerPageChange">
          <option :value="null">Default</option>
          <option :value="25">25</option>
          <option :value="50">50</option>
          <option :value="75">75</option>
          <option :value="100">100</option>
        </select>
        <span class="text-xs font-bold text-gray-600">{{ rangeStart }} – {{ rangeEnd }} of {{ pagination.totalElements }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { api } from "@/app/services/api.service";
import AppModal from "@/components/ui/AppModal.vue";
import { useToast } from "@/composables";

const ALL_PATH = "notifications/all";

type NotificationRow = Record<string, unknown>;

interface PaginationState {
  totalElements: number
  totalPages: number
  pageSize: number | null
}

function toBackendDateTime(localValue: string | null) {
  if (!localValue) return null;
  return localValue.replace("T", " ").substring(0, 16);
}

const { errorToast, warningToast } = useToast();

const loading = ref(false);
const currentPage = ref(1);
const pageInRequest = ref<number | null>(null);
const perPage = ref<number | null>(null);
const sort = ref<string | null>(null);
const notifications = ref<NotificationRow[]>([]);
const pagination = ref<PaginationState>({ totalElements: 0, totalPages: 0, pageSize: null });
const selectedNotification = ref<NotificationRow | null>(null);
const showDetail = ref(false);

const filters = ref({
  search: "",
  type: null as string | null,
  status: null as string | null,
  sender: null as string | null,
  startDateLocal: null as string | null,
  endDateLocal: null as string | null,
});

const sortOptions = [
  { value: null, text: "Sort" },
  { value: "asc", text: "Asc" },
  { value: "desc", text: "Desc" },
];
const typeOptions = [
  { value: null, text: "All types" },
  { value: "SMS", text: "SMS" },
  { value: "EMAIL", text: "Email" },
];
const statusOptions = [
  { value: null, text: "All statuses" },
  { value: "PENDING", text: "Pending" },
  { value: "SENT", text: "Sent" },
  { value: "FAILED", text: "Failed" },
];
const senderOptions = [
  { value: null, text: "All senders" },
  { value: "UGPASS", text: "UGPASS" },
  { value: "INSTACOM", text: "Instacom" },
  { value: "INFOBIP", text: "Infobip" },
  { value: "SMTP", text: "SMTP" },
];

const hasActiveFilters = computed(() => {
  const f = filters.value;
  return Boolean(
    f.search.trim() || f.type || f.status || f.sender || f.startDateLocal || f.endDateLocal
  );
});

const effectivePageSize = computed(() =>
  perPage.value != null ? perPage.value : pagination.value.pageSize
);

const rangeStart = computed(() => {
  const pageSize = effectivePageSize.value;
  if (pageSize == null) return 1;
  return (currentPage.value - 1) * pageSize + 1;
});

const rangeEnd = computed(() => {
  const pageSize = effectivePageSize.value;
  if (pageSize == null) {
    return pagination.value.totalElements || notifications.value.length;
  }
  return Math.min(currentPage.value * pageSize, pagination.value.totalElements);
});

const buildRequestParams = () => {
  const params: Record<string, string | number> = {};
  if (pageInRequest.value != null) params.page = pageInRequest.value - 1;
  if (perPage.value != null) params.perPage = perPage.value;
  if (sort.value != null) params.sort = sort.value;
  const trimmed = filters.value.search.trim();
  if (trimmed) params.search = trimmed;
  if (filters.value.type) params.type = filters.value.type;
  if (filters.value.status) params.status = filters.value.status;
  if (filters.value.sender) params.sender = filters.value.sender;
  const startDate = toBackendDateTime(filters.value.startDateLocal);
  const endDate = toBackendDateTime(filters.value.endDateLocal);
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  return params;
};

const extractList = (body: unknown): NotificationRow[] => {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  const obj = body as Record<string, unknown>;
  if (Array.isArray(obj.content)) return obj.content as NotificationRow[];
  if (Array.isArray(obj.records)) return obj.records as NotificationRow[];
  if (Array.isArray(obj.items)) return obj.items as NotificationRow[];
  if (Array.isArray(obj.notifications)) return obj.notifications as NotificationRow[];
  if (typeof obj.results === "object" && Array.isArray(obj.results)) return obj.results as NotificationRow[];
  return [];
};

const parsePageResponse = (root: unknown) => {
  if (root == null || typeof root !== "object") return { list: [] as NotificationRow[], meta: {} as Record<string, number | null> };
  if (Array.isArray(root)) return { list: root as NotificationRow[], meta: {} };
  const obj = root as Record<string, unknown>;
  const list = Array.isArray(obj.content) ? obj.content as NotificationRow[] : extractList(root);
  const pageable = obj.pageable as Record<string, unknown> | undefined;
  const pageSize =
    obj.size != null ? Number(obj.size)
      : pageable?.pageSize != null ? Number(pageable.pageSize)
        : null;
  const meta = {
    totalElements: obj.totalElements != null ? Number(obj.totalElements) : obj.total != null ? Number(obj.total) : 0,
    totalPages: obj.totalPages != null ? Number(obj.totalPages) : 0,
    number: obj.number != null ? Number(obj.number) : null,
    size: pageSize,
  };
  return { list, meta };
};

const applyPaginationMeta = (meta: Record<string, number | null>, listLen: number): PaginationState => {
  let totalElements = meta.totalElements || 0;
  let totalPages = meta.totalPages || 0;
  const pageSize = meta.size != null ? meta.size : perPage.value;
  if (meta.number != null) currentPage.value = meta.number + 1;
  if (totalElements === 0 && listLen > 0) {
    const offset = pageSize != null && meta.number != null ? meta.number * pageSize : 0;
    totalElements = offset + listLen;
  }
  if (pageSize != null && pageSize > 0 && totalPages === 0 && totalElements > 0) {
    totalPages = Math.ceil(totalElements / pageSize) || 1;
  }
  if (totalPages === 0) totalPages = listLen === 0 ? 0 : 1;
  return { totalElements, totalPages, pageSize: pageSize || null };
};

const fetch = () => {
  loading.value = true;
  api()
    .get(ALL_PATH, { params: buildRequestParams() })
    .then((response) => {
      const root = response.data as Record<string, unknown>;
      let list: NotificationRow[] = [];
      let meta: Record<string, number | null> = {};

      if (root != null && typeof root === "object" && root.data != null && typeof root.data === "object") {
        const parsed = parsePageResponse(root.data);
        list = parsed.list;
        meta = parsed.meta;
      } else {
        const parsed = parsePageResponse(root);
        list = parsed.list;
        meta = parsed.meta;
      }

      notifications.value = list;
      pagination.value = applyPaginationMeta(meta, notifications.value.length);
    })
    .catch((error) => {
      notifications.value = [];
      pagination.value = { totalElements: 0, totalPages: 0, pageSize: null };
      errorToast("Error", error.response?.data?.message || error.message || "Failed to load notifications");
    })
    .finally(() => {
      loading.value = false;
    });
};

const rowNumber = (idx: number) => {
  const pageSize = effectivePageSize.value;
  if (pageSize == null) return idx + 1;
  return (currentPage.value - 1) * pageSize + idx + 1;
};

const searchResetPage = () => {
  currentPage.value = 1;
  if (pageInRequest.value != null) pageInRequest.value = 1;
  fetch();
};

const resetFilters = () => {
  filters.value = {
    search: "",
    type: null,
    status: null,
    sender: null,
    startDateLocal: null,
    endDateLocal: null,
  };
  sort.value = null;
  currentPage.value = 1;
  pageInRequest.value = null;
  perPage.value = null;
  fetch();
};

const onPerPageChange = () => {
  currentPage.value = 1;
  if (pageInRequest.value != null) pageInRequest.value = 1;
  fetch();
};

const next = () => {
  if (currentPage.value < pagination.value.totalPages) {
    currentPage.value++;
    pageInRequest.value = currentPage.value;
    fetch();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

const previous = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    pageInRequest.value = currentPage.value;
    fetch();
  } else {
    warningToast("Observation", "No more data to show");
  }
};

const fieldValue = (row: NotificationRow | null, key: string) => {
  if (!row || typeof row !== "object") return null;
  const value = row[key];
  if (value != null && value !== "") return value;
  return null;
};

const notificationId = (row: NotificationRow) => {
  if (!row || typeof row !== "object") return null;
  if (row.id != null && row.id !== "") return row.id;
  if (row.notificationId != null && row.notificationId !== "") return row.notificationId;
  return null;
};

const inferredType = (row: NotificationRow) => {
  const explicit = fieldValue(row, "type");
  if (explicit) return explicit;
  const sender = String(fieldValue(row, "sender") || "").toUpperCase();
  if (sender === "SMTP") return "EMAIL";
  if (sender === "UGPASS" || sender === "INSTACOM" || sender === "INFOBIP") return "SMS";
  const receiver = receiverDisplay(row);
  if (receiver && receiver.indexOf("@") >= 0) return "EMAIL";
  if (receiver && receiver !== "—" && /^\d+$/.test(String(receiver).replace(/\D/g, ""))) return "SMS";
  return null;
};

const receiverDisplay = (row: NotificationRow | null) => {
  const r =
    fieldValue(row, "receiver") ||
    fieldValue(row, "recipient") ||
    fieldValue(row, "phoneNumber") ||
    fieldValue(row, "email");
  return r != null && String(r) !== "" ? String(r) : "—";
};

const displayKey = (row: NotificationRow, idx: number) => {
  const id = row?.id ?? row?.notificationId;
  if (id != null) return String(id);
  return `${row?.receiver}-${row?.status}-${row?.sender}-${idx}`;
};

const statusBadgeClass = (status: unknown) => {
  const key = status != null ? String(status).trim().toUpperCase().replace(/\s+/g, "_") : "";
  const variants: Record<string, string> = {
    PENDING: "gateway-status--pending",
    SENT: "gateway-status--sent",
    FAILED: "gateway-status--failed",
    DELIVERED: "gateway-status--sent",
    QUEUED: "gateway-status--pending",
  };
  return "gateway-status-badge " + (variants[key] || "gateway-status--default");
};

const formattedResponse = (row: NotificationRow | null) => {
  if (!row) return "";
  const resp = fieldValue(row, "response");
  if (!resp || resp === "null") return "—";
  try {
    const parsed = JSON.parse(String(resp));
    if (parsed === null) return "—";
    return JSON.stringify(parsed, null, 2);
  } catch (e) {
    return String(resp);
  }
};

const isFailed = (row: NotificationRow) =>
  String(fieldValue(row, "status") || "").toUpperCase() === "FAILED";

const responseSummary = (row: NotificationRow) => {
  const resp = fieldValue(row, "response");
  if (!resp || resp === "null") return "—";
  const text = String(resp).trim();
  try {
    const parsed = JSON.parse(text);
    if (parsed && typeof parsed === "object") {
      const record = parsed as Record<string, unknown>;
      const readable = record.message ?? record.error ?? record.description ?? record.reason;
      if (readable != null && readable !== "") return String(readable);
      return JSON.stringify(parsed);
    }
    return parsed == null ? "—" : String(parsed);
  } catch (e) {
    return text;
  }
};

const openDetail = (row: NotificationRow) => {
  selectedNotification.value = row;
  showDetail.value = true;
};

const refresh = () => fetch();

defineExpose({ refresh });
</script>

<style scoped>
.filter-label {
  display: block;
  margin-bottom: 0.25rem;
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
}

.filter-input {
  width: 100%;
  height: 38px;
  padding: 0 0.75rem;
  font-size: 14px;
  border: 1px solid #d1d5db;
  border-radius: 0.25rem;
  background-color: #fff;
}

.filter-input.pl-8 {
  padding-left: 2rem;
}

.detail-wrap .detail-row {
  margin-bottom: 0.5rem;
  font-size: 14px;
}

.detail-wrap .detail-label {
  font-weight: 600;
  margin-right: 0.5rem;
  min-width: 100px;
  display: inline-block;
}

.detail-wrap .detail-value-wrap {
  word-break: break-all;
}

.gateway-status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  font-size: 12px;
  font-weight: 600;
  border-radius: 4px;
  border: 1px solid transparent;
  line-height: 1.4;
}

.gateway-status--pending {
  background-color: #fff8e1;
  border-color: #ffb300;
  color: #e65100;
}

.gateway-status--sent {
  background-color: #e8f5e9;
  border-color: #43a047;
  color: #1b5e20;
}

.gateway-status--failed {
  background-color: #ffebee;
  border-color: #e53935;
  color: #b71c1c;
}

.gateway-status--default {
  background-color: #eceff1;
  border-color: #90a4ae;
  color: #455a64;
}
</style>
